/**
 * Error Handling Configuration
 *
 * Why: Different error handling behaviour for different environments
 * What: Centerlized configuration for all error-related settings
 *
 * Benefits:
 *      - Easy to adjust behaviour without code changes
 *      - Different settings for dev/statging/production
 *      - Single source of truth for error handling behavior
 */

import { envConfig } from "./env.config";

/**
 * Error configuration interface
 * Define all configurable error handling options
 *
 */

export interface ErrorConfig {
  /**
   * Logging Configuration
   */
  logging: {
    // Enable console logging
    enabled: boolean;
    /** Minimum log level to display */
    level: "debug" | "info" | "warn" | "error";
    /** Include stack traces in logs */
    includeStackTrace: boolean;
    /** Pretty print logs (dev only) */
    prettyPrint: boolean;
  };
  /**
   * Error Reporting Configuration (Sentry)
   */
  reporting: {
    /** Enable error reporting to external service */
    enabled: boolean;
    /** Sample rate for error reporting (0,1)*/
    sampleRate: number;
    /** Report unhandled promise rejections */
    captureUnhandledRejections: boolean;
    /** Report console errors */
    captureConsoleErrors: boolean;
  };
  /** Toast Notification configuration */
  toast: {
    /** Enable toast notifications */
    enabled: boolean;
    /** Default toast duration in ms */
    duration: number;
    /** position on screen */
    position: "top-right";
    /** Maximum number of toasts to show */
    maxToasts: number;
  };
  /** Retry Configuration */
  retry: {
    /** Maximum number of retry attempts */
    maxAttempts: number;
    /** Base delay between retries in ms */
    baseDelay: number;
    /** Maximum delay between retries in ms */
    maxDelay: number;
    /** Use exponential backoff */
    exponentialBackoff: boolean;
  };
  /**
   * Throttling Caonfiguration
   */
  throttle: {
    /** Enable error throttling */
    enabled: boolean;
    /** Time windows for throttling same errors in ms */
    windowMs: number;
    /** Maximum errors of same type in window */
    maxErrors: number;
  };
  /**
   * Ui Configuration
   */
  ui: {
    /** Show detailed error info in UI (dev only) */
    showDetailedErrors: boolean;
    /** Show error codes in messages */
    showErrorCodes: boolean;
    /** Enable retry buttons */
    enableRetryButtons: boolean;
  };
}

/**
 * Development configuration
 * Verbose logging, no external reporting detailed UI
 */
const developmentConfig: ErrorConfig = {
  logging: {
    enabled: true,
    level: "debug",
    includeStackTrace: true,
    prettyPrint: true,
  },
  reporting: {
    enabled: false,
    sampleRate: 1.0,
    captureUnhandledRejections: true,
    captureConsoleErrors: false,
  },
  toast: {
    enabled: true,
    duration: 5000,
    position: "top-right",
    maxToasts: 3,
  },
  retry: {
    maxAttempts: 1,
    baseDelay: 1000,
    maxDelay: 5000,
    exponentialBackoff: true,
  },
  throttle: {
    enabled: false, // Show all errors in dev
    windowMs: 1000,
    maxErrors: 1,
  },
  ui: {
    showDetailedErrors: true,
    showErrorCodes: true,
    enableRetryButtons: true,
  },
};

/**
 * Production configuration
 * Verbose logging, no external reporting detailed UI
 */
const producationConfig: ErrorConfig = {
  logging: {
    enabled: true,
    level: "error",
    includeStackTrace: false,
    prettyPrint: false,
  },
  reporting: {
    enabled: true,
    sampleRate: 1.0,
    captureUnhandledRejections: true,
    captureConsoleErrors: true,
  },
  toast: {
    enabled: true,
    duration: 5000,
    position: "top-right",
    maxToasts: 3,
  },
  retry: {
    maxAttempts: 2,
    baseDelay: 1000,
    maxDelay: 10000,
    exponentialBackoff: true,
  },
  throttle: {
    enabled: true, // Prevent error spam
    windowMs: 2000,
    maxErrors: 1,
  },
  ui: {
    showDetailedErrors: false,
    showErrorCodes: false,
    enableRetryButtons: true,
  },
};

/**
 * Select configuration based on environemet
 *
 */
const getConfig = (): ErrorConfig => {
  if (envConfig.isProduction) return producationConfig;
  return developmentConfig;
};

/**
 * Exported error configuration
 * Frozen to prevent accidental modification
 */

export const errorConfig: Readonly<ErrorConfig> = Object.freeze(getConfig());

/**
 * Helper to check if we should log at a given level
 */
export const shouldLog = (level: ErrorConfig["logging"]["level"]): boolean => {
  if (!errorConfig.logging.enabled) return false;

  const levels = ["debug", "info", "warn", "error"] as const;
  const configLevelIndex = levels.indexOf(errorConfig.logging.level);
  const checkLevelIndex = levels.indexOf(level);
  return checkLevelIndex >= configLevelIndex;
};
